import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useAxios from "../../../hooks/useAxios";
import "./ClassManagementPage.css";
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Button,
  Grid,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import Header from "../../../components/Header/Header";
import ContentHeader from "../../../components/common/ContentHeader/ContentHeader";
import QuizCard from "../../../components/QuizComponents/Cards/QuizCard/QuizCard";
import ConfirmationModal from "../../../components/common/ConfirmationModal/ConfirmationModal";
import { useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import AssessmentIcon from "@mui/icons-material/Assessment";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GradeBookTable from "../../../components/Reporting/GradeBookTable/GradeBookTable";
import ReportModal from "../../../components/Reporting/Modals/ReportModal/ReportModal";
import QuizManagementModal from "../../../components/DashboardComponents/Modals/QuizManagementModal/QuizManagementModal";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";

const navLinks = [];

function ClassManagementPage() {
  const navigate = useNavigate();
  const { classId } = useParams();
  const axiosInstance = useAxios();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [classData, setClassData] = useState(null);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isDeleteClassModalOpen, setIsDeleteClassModalOpen] = useState(false);
  const [deleteClassLoading, setDeleteClassLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [title, setTitle] = useState("Copy ID");
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [currentReport, setCurrentReport] = useState(null);
  const [studentsData, setStudentsData] = useState({});
  const [quizzesForTable, setQuizzesForTable] = useState();
  const [isQuizManagementModalOpen, setIsQuizManagementModalOpen] =
    useState(false);
  const [classComprehensiveReports, setClassComprehensiveReports] = useState(
    []
  );
  const [loadingComprehensiveReports, setLoadingComprehensiveReports] =
    useState(false);
  const [generatingComprehensiveReport, setGeneratingComprehensiveReport] =
    useState(false);
  const [selectedComprehensiveReport, setSelectedComprehensiveReport] =
    useState(null);
  const [showComprehensiveReportDetails, setShowComprehensiveReportDetails] =
    useState(false);
  const [deleteReportModalOpen, setDeleteReportModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deletingReport, setDeletingReport] = useState(false);
  const [isComprehensiveReportModalOpen, setIsComprehensiveReportModalOpen] =
    useState(false);
  const [comprehensiveReport, setComprehensiveReport] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Determine initial display count based on screen size
  let initialDisplayCount = 3;
  if (isSmallScreen) {
    initialDisplayCount = 1;
  } else if (isMediumScreen) {
    initialDisplayCount = 2;
  }

  const handleDeleteClass = () => {
    setIsDeleteClassModalOpen(true);
  };

  const handleCloseDeleteClassModal = () => {
    setIsDeleteClassModalOpen(false);
  };

  const handleConfirmDeleteClass = async () => {
    try {
      setDeleteClassLoading(true);
      const response = await axiosInstance.delete(`/classes/${classId}`);
      if (response.data.success) {
        toast.success("Class deleted successfully");
        navigate("/dashboard"); // Redirect to dashboard after deletion
      } else {
        toast.error(response.data.message || "Failed to delete class.");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "An unexpected error occurred while deleting the class."
      );
    } finally {
      setDeleteClassLoading(false);
      setIsDeleteClassModalOpen(false);
    }
  };

  const fetchClassComprehensiveReports = async () => {
    try {
      setLoadingComprehensiveReports(true);
      const response = await axiosInstance.get(
        `/reporting/classes/${classId}/comprehensive-report`
      );
      if (response.data.success) {
        // Handle both single report and array responses
        const reports = response.data.report
          ? [response.data.report]
          : response.data.reports || [];
        setClassComprehensiveReports(reports);
      } else {
        toast.error(
          response.data.message ||
            "Failed to fetch class comprehensive reports."
        );
      }
    } catch (err) {
      // If no reports exist yet, that's fine - just set empty array
      if (err.response?.status === 404) {
        setClassComprehensiveReports([]);
      } else {
        toast.error(
          err.response?.data?.message ||
            "An unexpected error occurred while fetching reports."
        );
      }
    } finally {
      setLoadingComprehensiveReports(false);
    }
  };

  const generateClassComprehensiveReport = async () => {
    try {
      setGeneratingComprehensiveReport(true);
      const response = await axiosInstance.post(
        `/ai-reports/classes/${classId}/generate-comprehensive-report`
      );
      if (response.data.success) {
        toast.success("Class comprehensive report generated successfully!");
        // Refresh the reports list
        await fetchClassComprehensiveReports();
      } else {
        toast.error(
          response.data.message ||
            "Failed to generate class comprehensive report."
        );
      }
    } catch (err) {
      // Handle specific error cases with user-friendly messages
      if (err.response?.status === 404) {
        const errorMessage =
          err.response.data?.message ||
          "Class has no completed sessions to analyze.";
        toast.error(`Cannot generate report: ${errorMessage}`);
      } else if (err.response?.status === 403) {
        toast.error(
          "You are not authorized to generate reports for this class."
        );
      } else if (err.response?.status >= 500) {
        toast.error(
          "Server error occurred while generating the report. Please try again later."
        );
      } else {
        toast.error(
          err.response?.data?.message ||
            "An unexpected error occurred while generating the report."
        );
      }
    } finally {
      setGeneratingComprehensiveReport(false);
    }
  };

  const handleViewComprehensiveReport = (report) => {
    setSelectedComprehensiveReport(report);
    setShowComprehensiveReportDetails(true);
  };

  const handleBackToComprehensiveReportsList = () => {
    setShowComprehensiveReportDetails(false);
    setSelectedComprehensiveReport(null);
  };

  // Handle delete report
  const handleDeleteReport = (report) => {
    setReportToDelete(report);
    setDeleteReportModalOpen(true);
  };

  const handleCloseDeleteReportModal = () => {
    setDeleteReportModalOpen(false);
    setReportToDelete(null);
  };

  const handleConfirmDeleteReport = async () => {
    if (!reportToDelete) return;

    setDeletingReport(true);
    try {
      const response = await axiosInstance.delete(
        `/reporting/classes/${classId}/comprehensive-report/${reportToDelete.id}`
      );
      if (response.data.success) {
        toast.success("Report deleted successfully!");
        // Remove the deleted report from the list
        setClassComprehensiveReports((prev) =>
          prev.filter((r) => r.id !== reportToDelete.id)
        );
        handleCloseDeleteReportModal();
      } else {
        toast.error(response.data.message || "Failed to delete report.");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          "An error occurred while deleting the report."
      );
    } finally {
      setDeletingReport(false);
    }
  };

  const fetchQuizzes = async () => {
    try {
      const response = await axiosInstance.get(`/classes/${classId}`);
      if (response.data.success) {
        const classInfo = response.data.class;
        const quizzesArray = classInfo.quizzes;
        const quizzesWithClassName = classInfo.quizzes.map((quiz) => ({
          ...quiz,
          class_name: classInfo.class_name,
        }));
        const studentsDataTemp = {};

        quizzesArray.forEach((quiz) => {
          quiz.sessions.forEach((session) => {
            const user = session.user;

            // Filter only students
            if (user.role !== "student") return;

            const userId = user.id;
            const fullName = user.profile
              ? user.profile.full_name
              : user.teacher_profile
              ? user.teacher_profile.full_name
              : user.username; // fallback

            // Initialize student data if not already present
            if (!studentsDataTemp[userId]) {
              studentsDataTemp[userId] = {
                id: userId,
                name: fullName,
                quizzes: {},
              };
            }

            // Add session attempts
            if (!studentsDataTemp[userId].quizzes[quiz.id]) {
              studentsDataTemp[userId].quizzes[quiz.id] = [];
            }
            studentsDataTemp[userId].quizzes[quiz.id].push({
              attempt: session.attempt,
              final_evaluation: session.final_evaluation,
              session_id: session.id,
            });
          });
        });

        setStudentsData(studentsDataTemp);

        const quizzesForTableTemp = classInfo.quizzes.map((q) => ({
          id: q.id,
          title: q.title,
        }));

        setQuizzesForTable(quizzesForTableTemp);
        setClassData(classInfo);
        setQuizzes(quizzesWithClassName);
        console.log(quizzesWithClassName);
        console.log(studentsDataTemp);
        console.log(quizzesForTableTemp);
      } else {
        toast.error(response.data.message || "Failed to fetch class data.");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuizzes();
    fetchClassComprehensiveReports();
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(classData?.unique_identifier || "");
    setTitle("Copied!");
  };

  const handleCopyClassReport = async () => {
    if (!selectedComprehensiveReport) return;

    const reportText = `
CLASS COMPREHENSIVE REPORT
==========================

Class: ${classData?.class_name || "N/A"}
Class ID: ${classData?.unique_identifier || "N/A"}
Generated Date: ${new Date(
      selectedComprehensiveReport.created_at
    ).toLocaleDateString()}

SUMMARY
-------
Total Sessions: ${selectedComprehensiveReport?.total_sessions}
Total Students: ${selectedComprehensiveReport?.total_students}
Total Quizzes: ${selectedComprehensiveReport?.total_quizzes}
Class Performance: ${selectedComprehensiveReport.class_performance}%

STRENGTH POINTS
---------------
${selectedComprehensiveReport.strength_points || "N/A"}

POINTS TO IMPROVE
-----------------
${selectedComprehensiveReport.points_to_improve || "N/A"}

RECOMMENDATION
--------------
${selectedComprehensiveReport.recommendation || "N/A"}
    `.trim();

    try {
      await navigator.clipboard.writeText(reportText);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error("Failed to copy report to clipboard:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = reportText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const handleCreateQuiz = () => {
    navigate("/quiz/create", { state: { classId } });
  };

  const handleEditQuiz = (quizId) => {
    navigate(`/quiz-review/${quizId}`);
  };

  const handleDeleteQuiz = (quizId) => {
    setSelectedQuiz(quizId);
    setIsConfirmationModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(`/quizzes/${selectedQuiz}`);
      if (response.data.success) {
        // Update quizzes after deletion
        fetchQuizzes();
        handleCloseConfirmationModal();
      } else {
        toast.error(response.data.message || "Failed to delete quiz.");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "An unexpected error occurred."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleViewReportClick = async (sessionId) => {
    try {
      const res = await axiosInstance.get(`/sessions/${sessionId}/report`);
      if (res.data.success) {
        setCurrentReport(res.data.report);
        setReportModalOpen(true);
      } else {
        toast.error(res.data.message || "Failed to fetch AI Report.");
      }
    } catch (err) {
      // Handle specific HTTP errors like 404
      if (err.response) {
        // Server responded with a status code out of the range of 2xx
        const errorMessage =
          err.response.data?.message || `Error: ${err.response.status}`;
        toast.error(errorMessage);
      } else if (err.request) {
        // No response received from server
        toast.error("No response received from the server. Please try again.");
      } else {
        // Other errors (e.g., client-side issues)
        toast.error(err.message || "An unexpected error occurred.");
      }
    }
  };

  const displayedQuizzes = showAll
    ? quizzes
    : quizzes.slice(0, initialDisplayCount);

  const totalQuizzes = quizzes.length;
  const isSubset = !showAll && totalQuizzes > displayedQuizzes.length;

  let content;
  if (loading) {
    content = (
      <Box className="loading-container">
        <CircularProgress />
        <Typography variant="h6" className="loading-text">
          Fetching Class Data...
        </Typography>
      </Box>
    );
  } else if (error) {
    content = (
      <Box className="error-container">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  } else {
    content = (
      <>
        {/* Class Info Section */}
        <Box className="class-info-card" sx={{ position: "relative" }}>
          <Box className="class-info-header">
            <Typography variant="h5" className="class-title">
              {classData.class_name}
            </Typography>
            <Box className="class-id-section">
              <Typography variant="body2" className="class-id-title">
                Class ID <br />{" "}
                <span className="class-id-value">
                  {classData.unique_identifier}
                </span>
              </Typography>
              <Tooltip title={title}>
                <IconButton onClick={handleCopy}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Typography variant="body1" className="class-description">
            {classData.description || "No description provided."}
          </Typography>

          {/* Delete Class Button - Positioned at bottom right */}
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              right: 16,
            }}
          >
            <Tooltip title="Delete Class" arrow placement="top">
              <Button
                className="delete-button"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClass}
                sx={{
                  backgroundColor: "#d32f2f",
                  "&:hover": {
                    backgroundColor: "#d32f2f",
                  },
                  boxShadow: "0 2px 8px rgba(211, 47, 47, 0.3)",
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                }}
              >
                Delete Class
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/* Quizzes Section */}
        <ContentHeader
          title="Class Assessments"
          // buttonLabel="Create Assessment"
          // onButtonClick={handleCreateQuiz}
          secondaryButtonLabel="Manage Assessments"
          onSecondaryButtonClick={() => setIsQuizManagementModalOpen(true)}
        />
        <Grid container spacing={3} className="quizzes-grid mb-5">
          {displayedQuizzes.length > 0 ? (
            displayedQuizzes.map((quiz) => (
              <Grid item key={quiz.id}>
                <QuizCard
                  quiz={quiz}
                  onEditClick={() => handleEditQuiz(quiz.id)}
                  onDeleteClick={() => handleDeleteQuiz(quiz.id)}
                  showAttachmentStatus={true}
                  isActiveInClass={quiz.is_active_in_class}
                  attachedAt={quiz.attached_at}
                />
              </Grid>
            ))
          ) : (
            <Typography>No Assessments available.</Typography>
          )}
        </Grid>
        {(isSubset || (showAll && totalQuizzes > initialDisplayCount)) && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={2}
            className="mb-5"
          >
            <Typography variant="body2" sx={{ mr: 2 }}>
              {showAll
                ? `Showing all ${totalQuizzes} Assessments`
                : `Showing ${displayedQuizzes.length} of ${totalQuizzes} Assessments`}
            </Typography>
            <Button
              className="text-button"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? "View Less" : "View All"}
            </Button>
          </Box>
        )}
        <ContentHeader title="Class Grade Book" />
        <GradeBookTable
          quizzes={quizzesForTable}
          studentsData={studentsData}
          onViewReport={handleViewReportClick}
        />
        <Box className="mt-5">
          <ContentHeader
            title="Class Comprehensive Reports"
            buttonLabel="Generate New Comprehensive Report"
            onButtonClick={generateClassComprehensiveReport}
          />
        </Box>

        {/* Class Comprehensive Reports Section */}
        <Box className="comprehensive-reports-section">
          {/* Generate New Report Button */}
          {/* <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={generateClassComprehensiveReport}
              disabled={generatingComprehensiveReport}
              startIcon={generatingComprehensiveReport ? <CircularProgress size={20} /> : <AssessmentIcon />}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5
              }}
            >
              {generatingComprehensiveReport ? "Generating Report..." : "Generate New Comprehensive Report"}
            </Button>
          </Box> */}

          {/* Reports Table */}
          {loadingComprehensiveReports ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : classComprehensiveReports.length > 0 ? (
            <TableContainer component={Paper} elevation={1} className="mb-5">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Generated Date</TableCell>
                    <TableCell>Total Sessions</TableCell>
                    <TableCell>Total Students</TableCell>
                    <TableCell>Total Quizzes</TableCell>
                    <TableCell>Class Performance</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {classComprehensiveReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell>
                        {new Date(report.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{report.total_sessions}</TableCell>
                      <TableCell>{report.total_students}</TableCell>
                      <TableCell>{report.total_quizzes}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={
                            report.class_performance > 75
                              ? "success.main"
                              : report.class_performance > 50
                              ? "warning.main"
                              : "error.main"
                          }
                          fontWeight={500}
                        >
                          {report.class_performance}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Report Details" arrow>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleViewComprehensiveReport(report)
                            }
                            color="primary"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Report" arrow>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteReport(report)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary">
                No comprehensive reports generated yet.
              </Typography>
              <Typography variant="body2" color="textSecondary" mt={1}>
                Click "Generate New Comprehensive Report" to create the first
                report for this class.
              </Typography>
            </Box>
          )}
        </Box>

        <ReportModal
          open={reportModalOpen}
          onClose={() => setReportModalOpen(false)}
          report={currentReport}
        />

        <QuizManagementModal
          open={isQuizManagementModalOpen}
          onClose={() => setIsQuizManagementModalOpen(false)}
          classId={classId}
          classData={classData}
          onQuizUpdate={fetchQuizzes}
          classQuizzes={quizzes}
        />

        {/* Comprehensive Report Modal */}
        {selectedComprehensiveReport && (
          <Dialog
            open={showComprehensiveReportDetails}
            onClose={handleBackToComprehensiveReportsList}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle className="student-report-modal-title">
              <Box display="flex" alignItems="center">
                {/* Left side: Back button and title */}
                <Box display="flex" alignItems="center" gap={2} flexGrow={1}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    className="report-modal-back-button"
                  >
                    {/* <Button onClick={handleBackToComprehensiveReportsList} size="small">
                      <ArrowBackIcon color="white" />
                    </Button> */}
                    <Typography variant="h6">
                      Class Comprehensive Report
                    </Typography>
                  </Box>
                </Box>
                {/* Right side: Close button */}
                <Box>
                  <Button
                    onClick={handleBackToComprehensiveReportsList}
                    className="student-report-modal-close-button"
                  >
                    <CloseIcon />
                  </Button>
                </Box>
              </Box>
            </DialogTitle>

            <DialogContent className="student-report-modal-content">
              {selectedComprehensiveReport && (
                <>
                  {/* Report Summary */}
                  <Box className="report-summary-section" mb={3}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6">Report Summary</Typography>
                      <Tooltip
                        title={
                          copySuccess ? "Copied!" : "Copy Report to Clipboard"
                        }
                        arrow
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<ContentCopyIcon />}
                          onClick={handleCopyClassReport}
                          color={copySuccess ? "success" : "primary"}
                          sx={{
                            minWidth: "auto",
                            px: 2,
                            ...(copySuccess && {
                              backgroundColor: "success.light",
                              color: "success.contrastText",
                              "&:hover": {
                                backgroundColor: "success.main",
                              },
                            }),
                          }}
                        >
                          {copySuccess ? "Copied!" : "Copy"}
                        </Button>
                      </Tooltip>
                    </Box>
                    <Box
                      className="student-info-section"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 3,
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                      }}
                    >
                      {/* Avatar or Icon */}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: "50%",
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: 28,
                            fontWeight: 700,
                            mr: 2,
                          }}
                        >
                          {classData?.class_name?.charAt(0).toUpperCase() ||
                            "C"}
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight={600}>
                            {classData?.class_name || "Class"}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Class ID:{" "}
                            <span
                              style={{
                                fontFamily: "monospace",
                                fontWeight: 500,
                              }}
                            >
                              {classData?.unique_identifier || "N/A"}
                            </span>
                          </Typography>
                        </Box>
                      </Box>
                      {/* Info Stats */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 4,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="caption" color="textSecondary">
                            Total Sessions
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            color="primary.main"
                          >
                            {selectedComprehensiveReport.total_sessions}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="caption" color="textSecondary">
                            Total Students
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            color="primary.main"
                          >
                            {selectedComprehensiveReport.total_students}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="caption" color="textSecondary">
                            Total Quizzes
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            color="primary.main"
                          >
                            {selectedComprehensiveReport.total_quizzes}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="caption" color="textSecondary">
                            Class Performance
                          </Typography>
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            color={
                              selectedComprehensiveReport.class_performance > 75
                                ? "success.main"
                                : selectedComprehensiveReport.class_performance >
                                  50
                                ? "warning.main"
                                : "error.main"
                            }
                          >
                            {selectedComprehensiveReport.class_performance}%
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>

                  {/* Report Content */}
                  <Box className="report-content-section">
                    {/* Strength Points */}
                    <Box className="report-section" mb={3}>
                      <Typography
                        variant="subtitle1"
                        className="report-label"
                        gutterBottom
                      >
                        Strength Points:
                      </Typography>
                      <Typography variant="body1" className="report-text">
                        {selectedComprehensiveReport.strength_points || "N/A"}
                      </Typography>
                    </Box>

                    {/* Points to Improve */}
                    <Box className="report-section" mb={3}>
                      <Typography
                        variant="subtitle1"
                        className="report-label"
                        gutterBottom
                      >
                        Points to Improve:
                      </Typography>
                      <Typography variant="body1" className="report-text">
                        {selectedComprehensiveReport.points_to_improve || "N/A"}
                      </Typography>
                    </Box>

                    {/* Recommendation */}
                    <Box className="report-section">
                      <Typography
                        variant="subtitle1"
                        className="report-label"
                        gutterBottom
                      >
                        Recommendation:
                      </Typography>
                      <Typography variant="body1" className="report-text">
                        {selectedComprehensiveReport.recommendation || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* Delete Report Confirmation Modal */}
        <ConfirmationModal
          open={deleteReportModalOpen}
          onClose={handleCloseDeleteReportModal}
          isDanger={true}
          title="Delete Comprehensive Report"
          subtitle={`Are you sure you want to delete this report?`}
          onSubmit={handleConfirmDeleteReport}
          submitText={deletingReport ? "Deleting..." : "Delete Report"}
          hasNotes={false}
        >
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            This action cannot be undone. The report will be permanently
            removed.
          </Typography>
        </ConfirmationModal>
      </>
    );
  }

  return (
    <>
      <Header navLinks={navLinks} isScrollEffect={false} />
      <Container className="class-management-container page-content">
        {content}
      </Container>
      <ConfirmationModal
        open={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        isDanger={true}
        title="Are you sure you want to delete this Assessment?"
        onSubmit={handleDelete}
        hasNotes={false}
      />

      <ConfirmationModal
        open={isDeleteClassModalOpen}
        onClose={handleCloseDeleteClassModal}
        isDanger={true}
        title="Delete Class"
        subtitle={`Are you sure you want to delete the class "${classData?.class_name}"?`}
        onSubmit={handleConfirmDeleteClass}
        submitText={deleteClassLoading ? "Deleting..." : "Delete Class"}
        hasNotes={false}
      >
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          This action cannot be undone. All class data, student enrollments,
          quiz attachments, and related information will be permanently removed.
        </Typography>
      </ConfirmationModal>

      <ToastContainer />
    </>
  );
}

export default ClassManagementPage;
